// src/hooks/useShiftValidation.js
import { useState, useEffect } from "react";

/**
 * Hook to validate if the user is within their allowed shift window for attendance actions.
 * @param {string} startTime - Shift start time (HH:mm)
 * @param {string} endTime - Shift end time (HH:mm)
 * @param {number} earlyMins - Allowed early check-in minutes (default 30)
 */
export function useShiftValidation(startTime, endTime, earlyMins = 30) {
  const [status, setStatus] = useState({
    isBeforeStart: false,
    isAfterEnd: false,
    isDuringShift: false,
    isWithinWindow: false,
    currentTimeStr: "",
  });

  useEffect(() => {
    const check = () => {
      const now = new Date();
      const currentMins = now.getHours() * 60 + now.getMinutes();
      const timeStr = now.toLocaleTimeString("en-IN", { hour12: false, hour: "2-digit", minute: "2-digit" });

      if (!startTime || !endTime) {
        setStatus({ isBeforeStart: false, isAfterEnd: false, isDuringShift: true, isWithinWindow: true, currentTimeStr: timeStr });
        return;
      }

      const [sh, sm] = startTime.split(":").map(Number);
      const [eh, em] = endTime.split(":").map(Number);

      const startTotalMins = sh * 60 + sm;
      const endTotalMins = eh * 60 + em;
      const allowedStartMins = startTotalMins - earlyMins;

      // Handle overnight shifts
      const isOvernight = endTotalMins < startTotalMins;

      let isBeforeStart, isAfterEnd, isDuringShift, isWithinWindow;

      if (!isOvernight) {
        isBeforeStart = currentMins < allowedStartMins;
        isAfterEnd = currentMins > endTotalMins;
        isDuringShift = currentMins >= startTotalMins && currentMins <= endTotalMins;
        isWithinWindow = currentMins >= allowedStartMins && currentMins <= endTotalMins;
      } else {
        // Overnight logic: 22:00 to 07:00
        // Window starts at 21:30
        // Window 1: 21:30 to 23:59
        // Window 2: 00:00 to 07:00
        const isWindow1 = currentMins >= allowedStartMins;
        const isWindow2 = currentMins <= endTotalMins;
        isWithinWindow = isWindow1 || isWindow2;
        
        const isDuring1 = currentMins >= startTotalMins;
        const isDuring2 = currentMins <= endTotalMins;
        isDuringShift = isDuring1 || isDuring2;

        isBeforeStart = currentMins < allowedStartMins && currentMins > endTotalMins;
        isAfterEnd = currentMins > endTotalMins && currentMins < allowedStartMins;
      }

      setStatus({
        isBeforeStart,
        isAfterEnd,
        isDuringShift,
        isWithinWindow,
        currentTimeStr: timeStr,
      });
    };

    check();
    const timer = setInterval(check, 30000); // Check every 30s
    return () => clearInterval(timer);
  }, [startTime, endTime, earlyMins]);

  return status;
}
